/**
 * Campaign Detail Page
 * View campaign details and manage campaign flights
 */

import React, { useState, useEffect, useMemo } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import FlightForm from "../components/campaigns/FlightForm";
import CampaignForm from "../components/campaigns/CampaignForm";
import DeleteCampaignButton from "../components/campaigns/DeleteCampaignButton";
import DeletedCampaignNotice from "../components/campaigns/DeletedCampaignNotice";
import { isCampaignVisible } from "../utils/campaignSoftDelete";
import { formatScheduledDateTime } from "../utils/formatters";
import { isSafeInternalPath } from "../utils/navigation";
import {
  getCampaign,
  getCampaignFlights,
  createFlight,
  updateCampaign,
  duplicateCampaign,
  getCampaignSummaryExport,
  CAMPAIGN_STATUS_OPTIONS,
  FLIGHT_STATUS_OPTIONS,
} from "../services/campaigns.service";
import {
  buildCampaignSummaryCsv,
  buildCampaignSummaryFilename,
  downloadCsv,
} from "../utils/campaignSummaryCsv";
import type {
  Campaign,
  CampaignFlight,
  CreateFlightDTO,
  UpdateCampaignDTO,
} from "../types/campaign.types";

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // R7 (2026-05-25): validate backTo through isSafeInternalPath to close
  // the open-redirect gap. Untrusted query params should never reach
  // navigate() raw; a crafted `?backTo=javascript:alert(1)` or
  // `?backTo=//evil.com` would break out of SPA routing.
  const [searchParams] = useSearchParams();
  const rawBackTo = searchParams.get("backTo");
  const backTo = isSafeInternalPath(rawBackTo) ? rawBackTo : null;

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [flights, setFlights] = useState<CampaignFlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showFlightForm, setShowFlightForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"flights" | "analytics">(
    "flights",
  );
  const [exporting, setExporting] = useState(false);

  const campaignFlightDateRange = useMemo(() => {
    const flightTimes = flights
      .map((flight) => flight.scheduled_send_at ?? flight.sent_at)
      .filter((value): value is string => Boolean(value))
      .map((value) => new Date(value).getTime())
      .filter((time) => !Number.isNaN(time));

    if (flightTimes.length === 0) {
      return {
        start: campaign?.start_date ?? null,
        end: campaign?.end_date ?? null,
      };
    }

    return {
      start: new Date(Math.min(...flightTimes)).toISOString(),
      end: new Date(Math.max(...flightTimes)).toISOString(),
    };
  }, [campaign?.end_date, campaign?.start_date, flights]);

  const handleDuplicateCampaign = async () => {
    if (!campaign) {
      return;
    }
    if (
      !window.confirm(
        `Duplicate "${campaign.name}" with all ${flights.length} flight(s) as a new draft campaign? Send history, recipients, vouchers, and tracking will NOT be cloned.`,
      )
    ) {
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const newCampaignId = await duplicateCampaign(campaign.id);
      navigate(`/campaigns/${newCampaignId}`);
    } catch (err) {
      console.error("Error duplicating campaign:", err);
      setError(
        err instanceof Error ? err.message : "Failed to duplicate campaign",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSummaryExport = async () => {
    if (!campaign || exporting) {
      return;
    }
    setExporting(true);
    try {
      const rows = await getCampaignSummaryExport(campaign.id);
      const csv = buildCampaignSummaryCsv(rows);
      const filename = buildCampaignSummaryFilename(campaign.name);
      downloadCsv(filename, csv);
      setSuccess(
        `Exported ${rows.length} flight${rows.length === 1 ? "" : "s"} to ${filename}`,
      );
    } catch (err) {
      console.error("Summary export failed:", err);
      setError(`Summary export failed: ${(err as Error).message}`);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadCampaign();
      loadFlights();
    }
  }, [id]);

  const loadCampaign = async () => {
    try {
      setLoading(true);
      const data = await getCampaign(id!);
      setCampaign(data);
    } catch (err) {
      console.error("Error loading campaign:", err);
      setError("Failed to load campaign");
    } finally {
      setLoading(false);
    }
  };

  const loadFlights = async () => {
    try {
      const data = await getCampaignFlights(id!);
      setFlights(data);
    } catch (err) {
      console.error("Error loading flights:", err);
    }
  };

  const handleCreateFlight = () => {
    setShowFlightForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleSubmitFlight = async (data: CreateFlightDTO) => {
    try {
      setSubmitting(true);
      setError(null);

      await createFlight(data);
      setSuccess(
        "Flight created successfully! Vouchers are being generated...",
      );
      setShowFlightForm(false);

      // Reload flights and campaign data
      loadFlights();
      loadCampaign();
    } catch (err) {
      console.error("Error creating flight:", err);
      setError("Failed to create flight");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelFlight = () => {
    setShowFlightForm(false);
    setError(null);
  };

  const handleUpdateCampaign = async (data: UpdateCampaignDTO) => {
    try {
      setSubmitting(true);
      setError(null);
      await updateCampaign(id!, data as UpdateCampaignDTO);
      setSuccess("Campaign updated successfully");
      setIsEditing(false);
      loadCampaign();
    } catch (err) {
      console.error("Error updating campaign:", err);
      setError("Failed to update campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (
    status: string,
    type: "campaign" | "flight" = "campaign",
  ) => {
    const campaignColors = {
      draft: "bg-gray-100 text-gray-800",
      active: "bg-green-100 text-green-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800",
    };

    const flightColors = {
      draft: "bg-gray-100 text-gray-800",
      scheduled: "bg-yellow-100 text-yellow-800",
      sending: "bg-blue-100 text-blue-800",
      sent: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
      failed: "bg-red-100 text-red-800",
    };

    const colors = type === "campaign" ? campaignColors : flightColors;
    const options =
      type === "campaign" ? CAMPAIGN_STATUS_OPTIONS : FLIGHT_STATUS_OPTIONS;
    const option = options.find((o) => o.value === status);

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status as keyof typeof colors] || "bg-gray-100 text-gray-800"}`}
      >
        {option?.label || status}
      </span>
    );
  };

  const formatDate = (date?: string | null) => {
    if (!date) {
      return "N/A";
    }
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) {
        return "N/A";
      }
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
          <p className="text-gray-600 mt-4">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || "Campaign not found"}
        </div>
        <button
          onClick={() => navigate("/campaigns")}
          className="mt-4 px-4 py-2 text-primary-600 hover:text-primary-700"
        >
          ← Back to Campaigns
        </button>
      </div>
    );
  }

  // Migration 203: a direct link to a soft-deleted campaign shows that it was deleted, not the campaign.
  if (!isCampaignVisible(campaign)) {
    return (
      <DeletedCampaignNotice
        campaign={campaign}
        onBack={() => navigate("/campaigns")}
      />
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(backTo || "/campaigns")}
          className="text-primary-600 hover:text-primary-700 flex items-center gap-1 mb-4"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {backTo?.startsWith("/events/")
            ? "Back to Event"
            : backTo
              ? "Back to Contact"
              : "Back to Campaigns"}
        </button>

        {/* Success/Error Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-900 hover:text-red-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
            <span>{success}</span>
            <button
              onClick={() => setSuccess(null)}
              className="text-green-900 hover:text-green-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {isEditing ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Edit Campaign
            </h2>
            <CampaignForm
              campaign={campaign}
              onSubmit={(data) =>
                handleUpdateCampaign(data as UpdateCampaignDTO)
              }
              onCancel={() => setIsEditing(false)}
              loading={submitting}
            />
          </div>
        ) : (
          <>
            {/* Header: title (wraps) on the left; ONE action row on the right,
                top-aligned with the title's first line. Below xl (where the
                sidebar leaves too little width for both side by side) the row
                stacks under the title and wraps instead of overflowing. */}
            <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between xl:gap-6">
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">
                  {campaign.name}
                </h1>
                {campaign.description && (
                  <p className="text-gray-600 mt-2">{campaign.description}</p>
                )}
              </div>
              <div
                className="flex flex-wrap items-center gap-2 xl:shrink-0 xl:justify-end xl:pt-px"
                data-testid="campaign-header-actions"
              >
                <button
                  onClick={handleDuplicateCampaign}
                  disabled={submitting}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                  title="Duplicate this campaign + all flights as draft"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  title="Edit campaign"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
                <DeleteCampaignButton
                  variant="button"
                  campaign={campaign}
                  flightStatuses={flights.map((f) => f.status)}
                  onDeleted={() => navigate("/campaigns")}
                />
                <button
                  onClick={handleSummaryExport}
                  disabled={exporting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download a CSV of all flights' current engagement metrics"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  {exporting ? "Exporting..." : "Summary Export"}
                </button>
                {getStatusBadge(campaign.status, "campaign")}
              </div>
            </div>
          </>
        )}
      </div>

      {!isEditing && (
        <>
          {/* Campaign Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500 mb-1">Total Flights</div>
              <div className="text-3xl font-bold text-gray-900">
                {campaign.total_flights}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500 mb-1">
                Vouchers Generated
              </div>
              <div className="text-3xl font-bold text-gray-900">
                {campaign.total_vouchers_generated}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500 mb-1">
                Vouchers Redeemed
              </div>
              <div className="text-3xl font-bold text-green-600">
                {campaign.total_vouchers_redeemed}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500 mb-1">Redemption Rate</div>
              <div className="text-3xl font-bold text-gray-900">
                {campaign.total_vouchers_generated > 0
                  ? Math.round(
                      (campaign.total_vouchers_redeemed /
                        campaign.total_vouchers_generated) *
                        100,
                    )
                  : 0}
                %
              </div>
            </div>
          </div>

          {/* Dates */}
          {(campaignFlightDateRange.start || campaignFlightDateRange.end) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 flex items-center gap-4 text-sm">
              {campaignFlightDateRange.start && (
                <div>
                  <span className="text-blue-700 font-medium">Start:</span>{" "}
                  {formatDate(campaignFlightDateRange.start)}
                </div>
              )}
              {campaignFlightDateRange.end && (
                <div>
                  <span className="text-blue-700 font-medium">End:</span>{" "}
                  {formatDate(campaignFlightDateRange.end)}
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex gap-6">
              <button
                onClick={() => setActiveTab("flights")}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "flights"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Flights
              </button>
              <button
                onClick={() => setActiveTab("analytics")}
                className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "analytics"
                    ? "border-primary-600 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                Analytics
              </button>
            </nav>
          </div>

          {activeTab === "flights" && (
            <>
              {/* Flight Form */}
              {showFlightForm && (
                <div className="mb-8 bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">
                    Create New Flight
                  </h2>
                  <FlightForm
                    campaignId={campaign.id}
                    siblingFlights={flights}
                    isEventCampaign={!!campaign.event_id}
                    onSubmit={handleSubmitFlight}
                    onCancel={handleCancelFlight}
                    loading={submitting}
                  />
                </div>
              )}

              {/* Flights Section */}
              {!showFlightForm && (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900">
                      Campaign Flights
                    </h2>
                    <button
                      onClick={handleCreateFlight}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Create Flight
                    </button>
                  </div>

                  {flights.length === 0 ? (
                    <div className="p-12 text-center">
                      <svg
                        className="w-16 h-16 text-gray-300 mx-auto mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No flights yet
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Create your first flight to start distributing vouchers
                      </p>
                      <button
                        onClick={handleCreateFlight}
                        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      >
                        Create Flight
                      </button>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {flights.map((flight) => {
                        const pct = (num: number, den: number) =>
                          den > 0 ? `${Math.round((num / den) * 100)}%` : "—";
                        const sent = flight.emails_sent ?? 0;
                        const delivered = flight.emails_delivered ?? 0;
                        const bounced = flight.emails_bounced ?? 0;
                        const pendingCount = Math.max(
                          0,
                          sent - delivered - bounced,
                        );
                        const deferred = flight.emails_deferred ?? 0;
                        const delRate = sent > 0 ? delivered / sent : 1;
                        const bncRate = sent > 0 ? bounced / sent : 0;
                        const delClass =
                          delRate >= 0.95
                            ? "text-green-700"
                            : delRate >= 0.85
                              ? "text-amber-600"
                              : "text-red-600";
                        const bncClass =
                          bncRate > 0.05
                            ? "text-red-600"
                            : bncRate > 0.02
                              ? "text-amber-600"
                              : "text-gray-900";
                        const hasIssue =
                          sent > 0 && (delRate < 0.9 || bncRate > 0.05);
                        return (
                          <Link
                            key={flight.id}
                            to={`/campaigns/${id}/flights/${flight.id}`}
                            className="block p-6 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {hasIssue && (
                                    <span
                                      className="mr-1 text-amber-500"
                                      title={`Needs a look: ${pct(delivered, sent)} delivered, ${pct(bounced, sent)} bounced`}
                                    >
                                      ⚠
                                    </span>
                                  )}
                                  {flight.name}
                                </h3>
                                {flight.description && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    {flight.description}
                                  </p>
                                )}
                              </div>
                              {getStatusBadge(flight.status, "flight")}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
                              <div>
                                <div className="text-gray-500">Recipients</div>
                                <div className="font-medium">
                                  {flight.total_recipients}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-500">
                                  Vouchers Generated
                                </div>
                                <div className="font-medium">
                                  {flight.vouchers_generated}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-500">Emails Sent</div>
                                <div className="font-medium">
                                  {flight.emails_sent}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-500">
                                  Emails Delivered
                                </div>
                                <div className={`font-medium ${delClass}`}>
                                  {delivered.toLocaleString()}{" "}
                                  <span className="opacity-70 font-normal">
                                    ({pct(delivered, sent)})
                                  </span>
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-500">Bounced</div>
                                <div className={`font-medium ${bncClass}`}>
                                  {bounced.toLocaleString()}{" "}
                                  <span className="opacity-70 font-normal">
                                    ({pct(bounced, sent)})
                                  </span>
                                </div>
                              </div>
                              <div>
                                <div
                                  className="text-gray-500"
                                  title="Sent minus delivered minus bounced — not yet reported by SendGrid (still processing/deferred)."
                                >
                                  Pending
                                </div>
                                <div className="font-medium">
                                  {pendingCount > 0 ? (
                                    <>
                                      {pendingCount.toLocaleString()}{" "}
                                      <span className="text-gray-400 font-normal">
                                        ({pct(pendingCount, sent)})
                                      </span>
                                    </>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </div>
                                {deferred > 0 && (
                                  <div
                                    className="text-xs text-amber-600 mt-0.5"
                                    title="Recipients the receiving provider is throttling (deferred). SendGrid auto-retries ~72h."
                                  >
                                    {deferred.toLocaleString()} throttled
                                  </div>
                                )}
                              </div>
                              <div>
                                <div className="text-gray-500">SMS Sent</div>
                                <div className="font-medium">
                                  {flight.sms_sent}
                                </div>
                              </div>
                            </div>

                            {flight.scheduled_send_at && (
                              <div className="mt-3 text-sm text-gray-600">
                                <span className="font-medium">Scheduled:</span>{" "}
                                {formatScheduledDateTime(
                                  flight.scheduled_send_at,
                                  (flight.metadata as any)?.send_timezone,
                                )}
                              </div>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === "analytics" && (
            <div className="space-y-6">
              {/* Campaign-level summary */}
              <div className="bg-white rounded-lg shadow">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">
                    Campaign Performance
                  </h2>
                </div>
                <div className="p-6">
                  {(() => {
                    const totals = flights.reduce(
                      (acc, f) => ({
                        emails_sent: acc.emails_sent + f.emails_sent,
                        emails_delivered:
                          acc.emails_delivered + f.emails_delivered,
                        emails_opened: acc.emails_opened + f.emails_opened,
                        emails_clicked: acc.emails_clicked + f.emails_clicked,
                        emails_bounced: acc.emails_bounced + f.emails_bounced,
                        sms_sent: acc.sms_sent + f.sms_sent,
                        sms_delivered: acc.sms_delivered + f.sms_delivered,
                        sms_failed: acc.sms_failed + f.sms_failed,
                        vouchers_generated:
                          acc.vouchers_generated + f.vouchers_generated,
                        vouchers_redeemed:
                          acc.vouchers_redeemed + f.vouchers_redeemed,
                      }),
                      {
                        emails_sent: 0,
                        emails_delivered: 0,
                        emails_opened: 0,
                        emails_clicked: 0,
                        emails_bounced: 0,
                        sms_sent: 0,
                        sms_delivered: 0,
                        sms_failed: 0,
                        vouchers_generated: 0,
                        vouchers_redeemed: 0,
                      },
                    );
                    const pct = (num: number, den: number) =>
                      den > 0 ? `${Math.round((num / den) * 100)}%` : "—";

                    return (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Emails Sent
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mt-1">
                            {totals.emails_sent}
                          </div>
                        </div>
                        {/* 2026-05-27 (Item C): Delivery Rate. Surfaces
                            the share of Sent that SendGrid confirmed as
                            delivered (didn't bounce). */}
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Delivery Rate
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mt-1">
                            {pct(totals.emails_delivered, totals.emails_sent)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Open Rate
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mt-1">
                            {pct(totals.emails_opened, totals.emails_sent)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Click Rate
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mt-1">
                            {pct(totals.emails_clicked, totals.emails_sent)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Bounce Rate
                          </div>
                          <div className="text-2xl font-bold text-gray-900 mt-1">
                            {pct(totals.emails_bounced, totals.emails_sent)}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                            Redemption Rate
                          </div>
                          <div className="text-2xl font-bold text-green-600 mt-1">
                            {pct(
                              totals.vouchers_redeemed,
                              totals.vouchers_generated,
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Per-flight performance table */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">
                    Per-Flight Performance
                  </h2>
                </div>
                {flights.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No flights to display analytics for.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Flight
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Sent
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Delivered
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Opened
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Clicked
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Bounced
                          </th>
                          <th
                            className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                            title="Sent minus delivered minus bounced — messages SendGrid hasn't reported as delivered or bounced yet (still processing/deferred)."
                          >
                            Pending
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            SMS Sent
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Vouchers
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Redeemed
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {flights.map((flight) => {
                          const pct = (num: number, den: number) =>
                            den > 0 ? `${Math.round((num / den) * 100)}%` : "—";
                          // Delivery breakdown so a user can read a flight's health without
                          // opening detail: sent = delivered + bounced + pending.
                          const sent = flight.emails_sent ?? 0;
                          const delivered = flight.emails_delivered ?? 0;
                          const bounced = flight.emails_bounced ?? 0;
                          const pendingCount = Math.max(
                            0,
                            sent - delivered - bounced,
                          );
                          const deferred = flight.emails_deferred ?? 0;
                          const delRate = sent > 0 ? delivered / sent : 1;
                          const bncRate = sent > 0 ? bounced / sent : 0;
                          const delClass =
                            delRate >= 0.95
                              ? "text-green-700"
                              : delRate >= 0.85
                                ? "text-amber-600"
                                : "text-red-600 font-semibold";
                          const bncClass =
                            bncRate > 0.05
                              ? "text-red-600 font-semibold"
                              : bncRate > 0.02
                                ? "text-amber-600"
                                : "text-gray-700";
                          // Flag a flight that a user would want to look at: low delivery or high bounce.
                          const hasIssue =
                            sent > 0 && (delRate < 0.9 || bncRate > 0.05);
                          return (
                            <tr key={flight.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {hasIssue && (
                                  <span
                                    className="mr-1 text-amber-500"
                                    title={`Needs a look: ${pct(delivered, sent)} delivered, ${pct(bounced, sent)} bounced`}
                                    aria-label="delivery issue"
                                  >
                                    ⚠
                                  </span>
                                )}
                                {flight.name}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {getStatusBadge(flight.status, "flight")}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {flight.emails_sent}
                              </td>
                              <td className="px-4 py-3 text-sm text-right">
                                <span className={delClass}>
                                  {delivered.toLocaleString()}{" "}
                                  <span className="opacity-70">
                                    ({pct(delivered, sent)})
                                  </span>
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {flight.emails_opened}{" "}
                                <span className="text-gray-400">
                                  (
                                  {pct(
                                    flight.emails_opened,
                                    flight.emails_sent,
                                  )}
                                  )
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {flight.emails_clicked}{" "}
                                <span className="text-gray-400">
                                  (
                                  {pct(
                                    flight.emails_clicked,
                                    flight.emails_sent,
                                  )}
                                  )
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right">
                                <span className={bncClass}>
                                  {bounced.toLocaleString()}{" "}
                                  <span className="opacity-70">
                                    ({pct(bounced, sent)})
                                  </span>
                                </span>
                              </td>
                              <td
                                className="px-4 py-3 text-sm text-right text-gray-700"
                                title="Not yet delivered or bounced (still processing/deferred at SendGrid)."
                              >
                                {pendingCount > 0 ? (
                                  <>
                                    {pendingCount.toLocaleString()}{" "}
                                    <span className="text-gray-400">
                                      ({pct(pendingCount, sent)})
                                    </span>
                                  </>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                                {deferred > 0 && (
                                  <div
                                    className="text-xs text-amber-600"
                                    title="Recipients the receiving provider is throttling (deferred). SendGrid auto-retries ~72h."
                                  >
                                    {deferred.toLocaleString()} throttled
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {flight.sms_sent}{" "}
                                <span className="text-gray-400">
                                  ({pct(flight.sms_delivered, flight.sms_sent)}{" "}
                                  delivered)
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {flight.vouchers_generated}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-700">
                                {flight.vouchers_redeemed}{" "}
                                <span className="text-gray-400">
                                  (
                                  {pct(
                                    flight.vouchers_redeemed,
                                    flight.vouchers_generated,
                                  )}
                                  )
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CampaignDetail;
